import dayjs from 'dayjs';
import { useMemo } from 'react';

import { schedule } from '@app/components/Schedule/data/schedule';
import type {
  ScheduleFormValues,
  ServerScheduleData,
} from '@app/components/Schedule/types';

const WEEK_DAYS_ORDER = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const useSchedule = () => {
  const initialScheduleValues = useMemo(
    () =>
      schedule.reduce(
        (acc, { label, active, startTime, endTime }) => ({
          ...acc,
          [label]: {
            active,
            startTime: dayjs(startTime, 'HH:mm'),
            endTime: dayjs(endTime, 'HH:mm'),
          },
        }),
        {} as ScheduleFormValues['schedule']
      ),
    []
  );

  const getScheduleDate = (
    schedule: ScheduleFormValues['schedule'],
    key: keyof ScheduleFormValues['schedule'],
    type: 'startTime' | 'endTime' = 'startTime'
  ) => {
    const day = schedule[key];
    if (day.active) {
      return dayjs(day[type]).format('HH:mm:ss');
    }
    return null;
  };

  const getScheduleDateForRobots = (
    schedule: ScheduleFormValues['schedule']
  ) => {
    const result: Record<number, { From: string; To: string }> = {};

    (WEEK_DAYS_ORDER as Array<keyof typeof schedule>).forEach((key, index) => {
      const day = schedule[key];

      if (day?.active) {
        result[index] = {
          From: dayjs(day.startTime).format('HH:mm:ss'),
          To: dayjs(day.endTime).format('HH:mm:ss'),
        };
      }
    });

    return result;
  };

  const setScheduleFormData = (data: ServerScheduleData) => {
    const updatedScheduleValues = { ...initialScheduleValues };

    Object.entries(initialScheduleValues).forEach(([key, defaultValue]) => {
      const upperCaseKey = (key.charAt(0).toUpperCase() +
        key.slice(1)) as keyof ServerScheduleData;
      const value = data[upperCaseKey];

      if (value) {
        // Если данные есть, обновляем active и время
        updatedScheduleValues[key as keyof ScheduleFormValues['schedule']] = {
          active: true,
          startTime: dayjs(value.From, 'HH:mm:ss'),
          endTime: dayjs(value.To, 'HH:mm:ss'),
        };
      } else {
        // Если данных нет, деактивируем день, оставив исходные времена
        updatedScheduleValues[key as keyof ScheduleFormValues['schedule']] = {
          ...defaultValue,
          active: false,
        };
      }
    });

    return updatedScheduleValues;
  };

  return {
    initialScheduleValues,
    getScheduleDate,
    getScheduleDateForRobots,
    setScheduleFormData,
  };
};
